<script setup lang="ts">
import { computed } from "vue";

export type EmbedThumbnailType = "sidebar" | "popover" | "popup" | "standard";

const props = withDefaults(
  defineProps<{
    type: EmbedThumbnailType;
    compact?: boolean;
    selected?: boolean;
  }>(),
  {
    compact: false,
    selected: false,
  }
);

const popoverCardStyle = computed(() => {
  if (props.type !== "popover") return undefined;
  return props.compact
    ? { top: "35%", right: "5%", width: "40%", height: "40%" }
    : { top: "40%", right: "5%", width: "40%", height: "36%" };
});
const popoverLauncherStyle = computed(() => {
  if (props.type !== "popover") return undefined;
  return props.compact
    ? { top: "81%", right: "5%", width: "7.3%", height: "12.8%" }
    : { top: "80%", right: "5%", width: "10%", height: "13%" };
});
</script>

<template>
  <span
    data-testid="embed-thumbnail"
    class="embed-type-thumbnail"
    :class="[
      `embed-type-thumbnail--${type}`,
      {
        'embed-type-thumbnail--compact': compact,
        'embed-type-thumbnail--selected': selected,
      },
    ]"
    aria-hidden="true"
  >
    <span class="embed-type-thumbnail__bar">
      <i class="embed-type-thumbnail__dot"></i>
      <i class="embed-type-thumbnail__dot"></i>
      <i class="embed-type-thumbnail__dot"></i>
    </span>
    <span class="embed-type-thumbnail__lines"></span>
    <span
      class="embed-type-thumbnail__widget"
      :style="popoverCardStyle"
    ></span>
    <span
      class="embed-type-thumbnail__accent"
      :style="popoverLauncherStyle"
    ></span>
  </span>
</template>

<style scoped>
.embed-type-thumbnail {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 13 / 10;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 8px;
  background: var(--base-white, #fff);
  box-shadow: none;
}
.embed-type-thumbnail--selected {
  border: 2px solid var(--primary-600, #155eef);
}
.embed-type-thumbnail__bar {
  position: relative;
  z-index: 3;
  display: flex;
  height: 25.4%;
  align-items: center;
  gap: 3.6%;
  box-sizing: border-box;
  padding: 0 8.7%;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
  background: var(--base-white, #fff);
}
.embed-type-thumbnail__dot {
  width: 5%;
  height: 25%;
  flex: 0 0 5%;
  border-radius: 50%;
  background: #f04438;
}
.embed-type-thumbnail__dot:nth-child(2) {
  background: #f79009;
}
.embed-type-thumbnail__dot:nth-child(3) {
  background: #12b76a;
}
.embed-type-thumbnail__lines {
  position: absolute;
  z-index: 1;
  top: 39%;
  left: 10%;
  width: 72%;
  height: 10%;
  border-radius: 999px;
  background: var(--gray-200, #eaecf0);
}
.embed-type-thumbnail__lines::before,
.embed-type-thumbnail__lines::after {
  position: absolute;
  left: 0;
  border-radius: 999px;
  background: inherit;
  content: "";
}
.embed-type-thumbnail__lines::before {
  top: 170%;
  width: 76%;
  height: 67%;
}
.embed-type-thumbnail__lines::after {
  top: 400%;
  width: 48%;
  height: 100%;
}
.embed-type-thumbnail__widget {
  position: absolute;
  z-index: 2;
  border-radius: 8px;
  background: #d1e0ff;
}
.embed-type-thumbnail__accent {
  position: absolute;
  z-index: 2;
  display: none;
  border-radius: 50%;
  background: #d1e0ff;
}
.embed-type-thumbnail--sidebar .embed-type-thumbnail__widget {
  inset: 25.4% auto 0 0;
  width: 35%;
  border-radius: 0;
}
.embed-type-thumbnail--sidebar .embed-type-thumbnail__lines {
  left: 40%;
  width: 54%;
}
.embed-type-thumbnail--popover .embed-type-thumbnail__lines {
  width: 62%;
}
.embed-type-thumbnail--popover .embed-type-thumbnail__accent {
  display: block;
}
.embed-type-thumbnail--popup {
  background: linear-gradient(
    to bottom,
    var(--base-white, #fff) 25.4%,
    #e4e4e7 25.4%
  );
}
.embed-type-thumbnail--popup .embed-type-thumbnail__lines {
  background: #d4d4d8;
}
.embed-type-thumbnail--popup .embed-type-thumbnail__widget {
  top: 64%;
  left: 50%;
  width: 41%;
  height: 47.5%;
  transform: translate(-50%, -50%);
}
.embed-type-thumbnail--standard .embed-type-thumbnail__widget {
  bottom: 6.8%;
  left: 50%;
  width: 48%;
  height: 30.5%;
  transform: translateX(-50%);
}
.embed-type-thumbnail--standard .embed-type-thumbnail__lines {
  width: 80%;
}
.embed-type-thumbnail--standard .embed-type-thumbnail__lines::before,
.embed-type-thumbnail--standard .embed-type-thumbnail__lines::after {
  display: none;
}
.embed-type-thumbnail--compact {
  aspect-ratio: 7 / 4;
  border-radius: 6px;
}
.embed-type-thumbnail--compact .embed-type-thumbnail__bar {
  gap: 2.2%;
  padding: 0 5%;
}
.embed-type-thumbnail--compact .embed-type-thumbnail__dot {
  width: 3.6%;
  flex-basis: 3.6%;
}
.embed-type-thumbnail--compact.embed-type-thumbnail--popover
  .embed-type-thumbnail__widget {
  border-radius: 6px;
}
</style>
